
// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://fozodmyenjkdedtghmpm.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZvem9kbXllbmprZGVkdGdobXBtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDIyNjE4MDksImV4cCI6MjA1NzgzNzgwOX0.ZrP4235dY74_2Eqhpvilml5ZS49lV5UkqPo9yFDd22A";

// Create a single instance of the supabase client to avoid multiple instances warning
let supabaseInstance: ReturnType<typeof createClient<Database>> | null = null;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";
export const supabase = (() => {
  if (supabaseInstance) return supabaseInstance;
  
  supabaseInstance = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce', // More secure PKCE flow for better token handling
    },
    global: {
      fetch: (...args) => {
        // Enhanced fetch with better error handling and timeout
        const [resource, config] = args;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
        
        return fetch(resource, {
          ...config,
          signal: controller.signal,
        }).then(response => {
          clearTimeout(timeoutId);
          
          // Log request details for debugging
          if (!response.ok) {
            console.error(`HTTP ${response.status} ${response.statusText} for ${resource}`);
          }
          
          return response;
        }).catch(error => {
          clearTimeout(timeoutId);
          
          // Enhanced error logging
          console.error('Fetch error for:', resource, error);
          
          // For network errors only, don't retry as it may cause issues
          if (error.name === 'AbortError') {
            console.warn('Request timeout for:', resource);
          }
          
          throw error;
        });
      }
    }
  });
  
  // Add custom functions error handling for edge functions
  const originalInvoke = supabaseInstance.functions.invoke;
  supabaseInstance.functions.invoke = async function(
    functionName: string,
    options?: { body?: any; headers?: Record<string, string>; }
  ) {
    try {
      console.log(`Invoking edge function: ${functionName}`);
      const result = await originalInvoke(functionName, options);
      
      // Log success for debugging
      console.log(`Function ${functionName} call completed:`, result);
      
      return result;
    } catch (error) {
      console.error(`Error invoking edge function ${functionName}:`, error);
      
      // Add contextual information to help debugging
      const enhancedError = {
        name: error.name || 'FunctionsFetchError',
        message: error.message || 'Unknown edge function error',
        functionName,
        context: {
          timestamp: new Date().toISOString(),
          online: navigator.onLine,
          // Include part of the body for debugging (but not sensitive data)
          bodyPreview: options?.body ? JSON.stringify(options.body).substring(0, 100) + '...' : 'No body'
        }
      };
      
      throw enhancedError;
    }
  };
  
  return supabaseInstance;
})();

// Helper function to get the storage key for auth tokens
export const getSupabaseAuthKey = (): string => {
  try {
    // Extract the hostname part from the URL and create the storage key
    const url = new URL(SUPABASE_URL);
    const projectRef = url.hostname.split('.')[0];
    return `sb-${projectRef}-auth-token`;
  } catch (e) {
    // Fallback if URL parsing fails
    console.error("Error parsing Supabase URL, using fallback method for auth key:", e);
    return `sb-${SUPABASE_URL.replace(/^(https?:)?\/\//, '').split('.')[0]}-auth-token`;
  }
};

// Helper for checking if refresh token exists
export const hasValidRefreshToken = (): boolean => {
  try {
    const storageKey = getSupabaseAuthKey();
    const authData = JSON.parse(localStorage.getItem(storageKey) || '{}');
    
    // Check for presence of refresh token
    return !!authData.refresh_token;
  } catch (error) {
    console.error("Error checking refresh token:", error);
    return false;
  }
};

// Helper to force refresh the current session
export const refreshSession = async (): Promise<boolean> => {
  try {
    // Only attempt refresh if we have a refresh token
    if (!hasValidRefreshToken()) {
      console.log("No refresh token available for session refresh");
      return false;
    }
    
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error("Session refresh error:", error);
      return false;
    }
    
    return !!data.session;
  } catch (error) {
    console.error("Error during session refresh:", error);
    return false;
  }
};

// Helper to check for development mode
export const isDevelopmentMode = (): boolean => {
  return localStorage.getItem('development_mode') === 'true';
};

// Clear all auth data for clean logout
export const clearAuthData = (): void => {
  try {
    // Get the auth key
    const authKey = getSupabaseAuthKey();
    
    // Clear specific auth tokens
    localStorage.removeItem(authKey);
    sessionStorage.removeItem(authKey);
    
    // Clear any other auth-related items
    const keysToRemove = [];
    
    // Find all auth-related keys in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.includes('supabase.auth.') || 
        key.includes('sb-') || 
        key.startsWith('googleAuth') ||
        key.includes('_token')
      )) {
        keysToRemove.push(key);
      }
    }
    
    // Remove identified keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    console.log("Auth data cleared");
  } catch (error) {
    console.error("Error clearing auth data:", error);
  }
};
